import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { makeEncoderFromCodec, makeRoute } from "trader.ts/route";
import { Source } from "trader.ts/types/source";
import { dyn, dynStyle, dynVariadic } from "trader.ts/ui";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  formulaid,
  formula_color,
  instrumentid,
  internalteacherid,
  lessongroupid,
  lessonid,
  roomid,
  siteid,
  teacherid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as planningstyle from "../../../styles/planning.css.classes";
import * as style from "../../../styles/style.css.classes";
import { makeClientsideRouter } from "../../client/clientsiderouter";
import { renderWeekCalendar } from "../../client/renderweekcalendar";
import { period } from "../../domain/periods";
import { colors, formulaColorToPlanningColors } from "../../lib/colors";
import { makeDateRange } from "../../lib/time";
import { HHmm, renderFirstNameLastName } from "../../render";
import * as routes from "../../routes";
import { translateWeekday } from "../../translations_specific";
import { renderPlanningDateButtons } from "../planning/aside";
import { renderClosePopupButton } from "../planning/selectionandforms";
import {
  calcSelectedElementDimensionsForCalendarSlot,
  renderFloatingPopup,
} from "../planning/floatingpopup";
import { parseFormulaColor } from "../../parsing_from_ur";
import { printStylePlanning } from "../../lib/printstyle";

type lesson = {
  lessongroup: {
    id: lessongroupid;
    lessonduration: number;
  };
  lesson: {
    id: lessonid;
    time: joda.LocalTime;
    date: joda.LocalDate;
  };
  teacher: {
    id: teacherid;
    internalteacherid: internalteacherid;
    firstname: string;
    lastname: string;
  };
  formula: {
    id: formulaid;
    description: string;
    color: formula_color;
  };
  instrument: {
    id: instrumentid;
    description: string;
  };
  room: {
    id: roomid;
    description: string;
    siteid: siteid;
  };
};

export type mode =
  | { tag: "normal" }
  | {
      tag: "selected_lesson";
      weekday: joda.DayOfWeek;
      time: joda.LocalTime;
      roomid: roomid;
      lessonid: lessonid;
    };

const modeC = routes.magicCodec<mode>();
const modeRoute = makeRoute("/mode/{mode:mode}", {
  mode: makeEncoderFromCodec(modeC),
});
const router = makeClientsideRouter(modeRoute, { tag: "normal" });

export function render(opts: {
  period: period;
  filter: roomid[];
  monday: joda.LocalDate;
  sunday: joda.LocalDate;
  lessons: lesson[];
  allrooms: { id: roomid; description: string }[];
  schoolhours: [joda.LocalTime, joda.LocalTime];
}): HTMLElement | HTMLElement[] {
  const modeS: Source<mode> = router.start();
  const filterS = new Source(opts.filter);
  const slotsS = new Source(opts.lessons);
  async function reloadSlots() {
    slotsS.set(
      await rpc(routes.loadRoomplanningSlotsSpec, undefined, {
        monday: opts.monday,
        sunday: opts.sunday,
        roomsfilter: filterS.get(),
      }),
    );
  }

  const aside = renderAside(opts, filterS, reloadSlots);
  const today = joda.LocalDate.now();

  const hours: [number, number] = [
    opts.schoolhours[0].hour(),
    opts.schoolhours[1].hour() + (opts.schoolhours[1].minute() > 0 ? 1 : 0),
  ];
  const calendar = dynVariadic(
    [slotsS, modeS, filterS],
    ([slots, mode, filter]) =>
      renderWeekCalendar({
        hours,
        weekdays: makeDateRange(opts.monday, opts.sunday).map((d) => [
          d.dayOfWeek(),
          {
            label: d.dayOfMonth().toString(),
            sublabel: translateWeekday(d.dayOfWeek()).slice(0, 2),
            link: undefined,
            contents: d.isBefore(today)
              ? { tag: "past" }
              : d.isEqual(today)
              ? { tag: "today" }
              : null,
          },
        ]),
        alwaysShowGroups: false,
        groupByTeachers: opts.allrooms
          .filter((r) => filter.includes(r.id))
          .map((r) => ({
            internalteacherid: r.id as unknown as internalteacherid,
            firstname: r.description,
          })),
        collapseEmptyTeachers: true,
        slots: slots.map((l) => {
          const selectedClass =
            mode.tag === "selected_lesson" && mode.lessonid === l.lesson.id
              ? planningstyle.selected
              : "";
          const notSelectedClass =
            mode.tag === "normal"
              ? ""
              : l.lesson.id !== mode.lessonid
              ? style.seethrough
              : "";
          return {
            weekday: l.lesson.date.dayOfWeek(),
            time: l.lesson.time,
            duration: l.lessongroup.lessonduration,
            contents: l.teacher.firstname,
            colors: formulaColorToPlanningColors(
              parseFormulaColor(l.formula.color),
            ),
            onclick: {
              href: router.makeHashLink({
                tag: "selected_lesson",
                weekday: l.lesson.date.dayOfWeek(),
                time: l.lesson.time,
                roomid: l.room.id,
                lessonid: l.lesson.id,
              }),
            },
            className: selectedClass + notSelectedClass,
            internalteacherid: l.room.id as unknown as internalteacherid, // hack
          };
        }),
        onClickOutsideSlot: () => {
          document.location.hash = router.makeHashLink({
            tag: "normal",
          });
        },
      }),
  );

  function goToNormal() {
    document.location.hash = router.makeHashLink({
      tag: "normal",
    });
  }

  const popup = renderFloatingPopup(
    modeS,
    slotsS,
    null,
    (mode, slots) => {
      if (mode.tag === "normal") {
        return null;
      } else if (mode.tag === "selected_lesson") {
        return slots.find((l) => l.lesson.id === mode.lessonid);
      } else {
        return checkAllCasesHandled(mode);
      }
    },
    {
      getApproximateFloatingPopupHeight: () => 600,
      renderPopupContents: (lesson) => {
        const colors = formulaColorToPlanningColors(
          parseFormulaColor(lesson.formula.color),
        );
        return renderLessonDetails(colors, goToNormal, lesson);
      },
      getSelectedElementDimensions: function (s: lesson) {
        return calcSelectedElementDimensionsForCalendarSlot(hours[0], {
          weekday: s.lesson.date.dayOfWeek(),
          time: s.lesson.time,
          duration: s.lessongroup.lessonduration,
        });
      },
      calcPopupWidth: () => 480,
      modeIsDayView: () => false,
      offsetRight: 8,
      offsetLeft: 8 + 35,
    },
  );

  return [
    dynStyle(
      modeS,
      (mode) =>
        // We disable scrolling, as popup is positioned fixed
        mode.tag === "selected_lesson" ? "overflow: hidden;" : "",
      <main
        style={`
  max-height: var(--viewportminusnav);
  overflow-y: auto;
  max-width: unset;
`}
      >
        {calendar}
        {popup}
        {printStylePlanning()}
      </main>,
    ),
    aside,
  ];
}

function renderLessonDetails(
  colors: colors,
  goToNormal: () => void,
  lesson: lesson,
): HTMLElement | HTMLElement[] {
  return (
    <div
      className={style.panel}
      style={
        "margin-bottom: 0px;" +
        "box-shadow: 0px 2px 8px 4px rgba(0, 0, 0, 0.3); overflow-y: auto; "
      }
    >
      <div
        className={style.panel_header}
        style={`background: ${colors.primary}; color: ${colors.text}`}
      >
        {renderClosePopupButton(goToNormal)}
        <article className={style.text_with_icon}>
          <i className={FA.icon_glasses}></i>
          <div>
            <b>{renderFirstNameLastName(lesson.teacher)}</b>
          </div>
        </article>

        <div style="display: flex">
          <div style="flex: 1" className={style.text_with_icon}>
            <i className={FA.icon_clipboard_solid}></i>
            <div>{lesson.formula.description}</div>
          </div>
          <div style="text-align: right" className={style.text_with_icon}>
            <div>
              {lesson.lesson.time.format(HHmm) +
                " - " +
                lesson.lesson.time
                  .plusMinutes(lesson.lessongroup.lessonduration)
                  .format(HHmm)}
            </div>
            <i className={FA.icon_clock_regular + style.icon_on_right}></i>
          </div>
        </div>
      </div>

      <section style="display: flex">
        <div style="flex: 1" className={style.text_with_icon}>
          <i className={FA.icon_music_solid}></i>
          <div>{lesson.instrument.description}</div>
        </div>
        <div style="text-align: right" className={style.text_with_icon}>
          <div>{lesson.room.description}</div>
          <i className={FA.icon_map_marker_alt_solid + style.icon_on_right}></i>
        </div>
      </section>

      <a
        className={style.text_with_icon}
        href={routes.lessongroup_generalRoute.link({
          lessongroupid: lesson.lessongroup.id,
        })}
      >
        <i className={FA.icon_link_solid}></i>
        <div>{"Details lessenreeks"}</div>
      </a>
    </div>
  );
}

function renderAside(
  opts: {
    period: period;
    monday: joda.LocalDate;
    sunday: joda.LocalDate;
    allrooms: { id: roomid; description: string }[];
  },
  filterS: Source<roomid[]>,
  reloadSlots: () => {},
) {
  const labelStyle = `color: var(--grey-900); font-weight: 600;`;

  function setFilter(pf: roomid[]) {
    filterS.set(pf);
    void rpc(routes.setRoomplanningFilterCookieSpec, undefined, pf);
    reloadSlots();
  }

  const content = (
    <div>
      {renderPlanningDateButtons(opts.period, opts.monday, opts.sunday, (d) =>
        routes.roomplanningRoute.link({
          roomid: undefined,
          date: d || undefined,
        }),
      )}

      {dyn(filterS, function (filter) {
        const rooms = (
          <section>
            <h1 style={labelStyle}>{"Lokalen"}</h1>
            {opts.allrooms
              .orderBy((r) => r.description)
              .map((room) => {
                return (
                  <label
                    className={
                      style.checkbox + planningstyle.checkbox_as_button
                    }
                  >
                    <input
                      type="checkbox"
                      checked={filter.includes(room.id)}
                      onclick={(ev) => {
                        if (ev.ctrlKey || ev.metaKey || ev.altKey) {
                          const curr = filterS.get();
                          if (curr.includes(room.id)) {
                            setFilter(curr.filter((id) => id !== room.id));
                          } else {
                            setFilter(curr.concat(room.id).uniq());
                          }
                        } else {
                          setFilter([room.id]);
                        }
                      }}
                    />
                    <span>{room.description}</span>
                  </label>
                );
              })}

            <label
              className={style.checkbox + planningstyle.checkbox_as_button}
            >
              <input
                type="checkbox"
                checked={filter.length === opts.allrooms.length}
                onclick={() => {
                  setFilter(opts.allrooms.map((r) => r.id));
                }}
              />
              <span>{"Allemaal"}</span>
            </label>

            <div style="font-size: 13px;padding-top: 0px;">
              {"Ctrl + klik om meerdere te selecteren"}
            </div>
          </section>
        );
        return rooms;
      })}
    </div>
  );

  return (
    <aside style="max-height: var(--viewportminusnav); overflow-y: auto; border-left: 1px solid var(--grey-300);">
      {content}
    </aside>
  );
}
